<template>
  <div>
    <RightSideBar
      v-if="isOpen"
      :loading="isLoading"
      @close="isOpen = false"
      width="width:42rem"
    >
      <template v-slot:title>
        <p class="text-darkPurple font-bold">
          View Details
        </p>
      </template>
      <template v-slot:subtitle>
        <div class="border-b border-borderGrey mb-5 pb-5">
          <p class="font-semibold text-sm text-darkPurple mb-3 uppercase">Employee Details (Old)</p>
          <div class="flex justify-between items-start w-full gap-3">
            <div class="w-4/12 rounded-md text-blueCrayola font-normal">
              <img
                class="h-40 w-full rounded-md object-cover"
                v-if="employee.photo"
                :src="employee.photo"
                alt="profile"
              />
              <div v-else class="h-40 border rounded-md flex justify-center items-center">
                <span class="text-3xl font-semibold">
                  {{ $getInitials(`${employee.fname || ''} ${employee.lname || ''}`) || '-' }}
                </span>
              </div>
            </div>
            <div class="w-8/12 flex flex-col justify-start items-start gap-2">
              <h4 class="font-bold text-lg leading-5 text-darkPurple">
                {{ displayValue(employee.fname) }} {{ displayValue(employee.lname) }}
              </h4>
              <div class="flex flex-col justify-start items-center w-full gap-1.5">
                <div
                  class="flex justify-start items-start w-full"
                  v-for="field in employeeFields"
                  :key="field.label"
                >
                  <p class="w-2/5 font-extrabold text-xs uppercase leading-5 text-romanSilver">
                    {{ field.label }}:
                  </p>
                  <p class="w-3/5 font-semibold text-sm leading-5 text-darkPurple">
                    {{ field.value }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template>
        <div class="flex flex-col justify-start items-start gap-4 w-full">
          <p class="font-semibold text-sm text-darkPurple uppercase">Request Details</p>

          <div class="w-full flex flex-col gap-2">
            <p class="text-jet text-sm">New Contract End Date</p>
            <div class="date-readonly flex items-center gap-2 px-3">
              <Icon icon-name="calendar" class-name="text-romanSilver flex-shrink-0" size="xs" />
              <span class="text-sm text-jet">{{ formatDate(request.newContractEndDate, "dd - MM - yyyy") }}</span>
            </div>
          </div>

          <div class="w-full flex flex-col gap-2">
            <p class="text-jet text-sm">Additional Note</p>
            <div class="note-box text-sm text-jet">
              {{ displayValue(request.note) }}
            </div>
          </div>

          <div class="w-full flex flex-col gap-2">
            <p class="text-jet text-sm">Attachment</p>
            <div
              v-if="request.fileUrl"
              class="flex items-start gap-2 cursor-pointer"
              @click="openPreview"
            >
              <Icon
                v-if="isPdfAttachment"
                icon-name="icon-pdf"
                size="m"
                class-name="flex-shrink-0"
              />
              <Icon v-else icon-name="doc" size="m" class-name="flex-shrink-0" />
              <div class="flex flex-col min-w-0">
                <span class="text-sm text-blueCrayola font-semibold break-all">{{ attachmentName }}</span>
                <span class="text-xs text-blueCrayola">
                  {{ attachmentLabel }}
                  <span v-if="attachmentSize"> • {{ attachmentSize }}</span>
                </span>
              </div>
            </div>
            <p v-else class="text-sm text-jet">-</p>
          </div>

          <div class="w-full grid grid-cols-2 gap-2 mt-2">
            <Card class="flex justify-start items-start gap-2 p-3">
              <Icon icon-name="calendar" class="text-romanSilver mt-1" size="xs" />
              <div class="flex flex-col justify-start items-start">
                <h6 class="font-normal text-xs text-blueCrayola">Created</h6>
                <p class="font-semibold text-jet text-xs uppercase">{{ formatDate(request.createdAt, "MMM dd, yyyy") }}</p>
              </div>
            </Card>
            <Card class="flex justify-start items-start gap-2 p-3">
              <Icon icon-name="icon-grid" class="text-romanSilver mt-1" size="xs" />
              <div class="flex flex-col justify-start items-start">
                <h6 class="font-normal text-xs text-carrotOrange">Initiated By</h6>
                <p class="font-semibold text-jet text-xs uppercase truncate">{{ initiatorName }}</p>
              </div>
            </Card>
            <Card class="flex justify-start items-start gap-2 p-3">
              <Icon icon-name="icon-grid" class="text-romanSilver mt-1" size="xs" />
              <div class="w-full flex flex-col justify-start items-start">
                <h6 class="font-normal text-xs text-flame">Approval Status</h6>
                <div class="flex justify-between w-full items-center gap-2">
                  <p class="font-semibold text-jet text-xs uppercase">
                    {{ formatStatus(request.status) }}
                  </p>
                  <span
                    v-if="request.id"
                    class="text-blueCrayola cursor-pointer whitespace-nowrap underline"
                    style="font-size:10px"
                    @click="$emit('trail', request.id)"
                  >
                    View Trail
                  </span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </template>
      <template v-slot:footer>
        <Button
          width="7.875rem"
          class="font-semibold bg-flame text-white"
          @click="isOpen = false"
        >
          Close
        </Button>
      </template>
    </RightSideBar>

    <Modal v-if="showPreview">
      <Card class="bg-white p-5 flex flex-col gap-4 preview-modal">
        <div class="flex justify-between items-center gap-3">
          <p class="text-darkPurple font-bold truncate">{{ attachmentName }}</p>
          <Icon
            icon-name="icon-close"
            size="xs"
            class-name="text-romanSilver cursor-pointer"
            @click.native="showPreview = false"
          />
        </div>
        <div class="preview-body w-full flex justify-center items-center overflow-auto">
          <img
            v-if="isImageAttachment"
            :src="request.fileUrl"
            alt="attachment preview"
            class="max-w-full max-h-full object-contain rounded"
          />
          <iframe
            v-else-if="isPdfAttachment"
            :src="request.fileUrl"
            class="w-full h-full border-0 rounded"
            title="PDF preview"
          />
          <div v-else class="flex flex-col items-center gap-3 py-10">
            <Icon icon-name="doc" size="l" class-name="text-blueCrayola" />
            <a
              :href="request.fileUrl"
              target="_blank"
              rel="noopener"
              class="text-sm text-blueCrayola underline"
            >
              Open attachment
            </a>
          </div>
        </div>
      </Card>
    </Modal>
  </div>
</template>

<script>
import Card from "@scelloo/cloudenly-ui/src/components/card";
import RightSideBar from "@/components/RightSideBar";
import Modal from "@/components/Modal";
import Button from "@/components/Button";

export default {
  name: "ContractEmployeeDetails",
  components: {
    Card,
    RightSideBar,
    Modal,
    Button,
  },
  data() {
    return {
      isLoading: true,
      isOpen: false,
      showPreview: false,
      employee: {},
      request: {},
    };
  },
  computed: {
    employeeFields() {
      return [
        { label: "Organisational Unit", value: this.displayValue(this.employee.function) },
        { label: "Position", value: this.displayValue(this.employee.designation) },
        { label: "Job Level", value: this.displayValue(this.employee.level) },
        { label: "Office Location", value: this.displayValue(this.employee.officeLocation || this.employee.employeeOffice?.name) },
        { label: "Reporting To", value: this.displayValue(this.reportingToName) },
        { label: "Contract End Date", value: this.formatDate(this.employee.contractEndDate || this.request.contractEndDate, "MMM dd, yyyy") },
      ];
    },
    reportingToName() {
      const manager = this.employee.userReportingTo || this.employee.reportingTo;
      if (!manager) return "";
      if (typeof manager === "string") return manager;
      return [manager.fname, manager.lname].filter(Boolean).join(" ");
    },
    initiatorName() {
      const initiator = this.request.initiator;
      if (!initiator) return "-";
      const name = [initiator.fname, initiator.lname].filter((part) => part && String(part).trim()).join(" ");
      return name || "-";
    },
    attachmentName() {
      if (!this.request.fileUrl) return "-";
      try {
        return decodeURIComponent(this.request.fileUrl.split("/").pop());
      } catch (e) {
        return this.request.fileUrl.split("/").pop();
      }
    },
    attachmentLabel() {
      return this.attachmentName.replace(/\.[^/.]+$/, "").toLowerCase();
    },
    attachmentSize() {
      return this.request.fileSize || "";
    },
    attachmentExtension() {
      return (this.attachmentName.split(".").pop() || "").toLowerCase();
    },
    isImageAttachment() {
      return ["jpg", "jpeg", "png"].includes(this.attachmentExtension);
    },
    isPdfAttachment() {
      return this.attachmentExtension === "pdf";
    },
  },
  methods: {
    displayValue(value) {
      if (value === null || value === undefined || String(value).trim() === "") return "-";
      return value;
    },
    formatDate(date, pattern = "MMMM dd, yyyy") {
      if (!date) return "-";
      return this.$DATEFORMAT(new Date(date), pattern);
    },
    formatStatus(status) {
      if (!status) return "-";
      return status.split("_").join("-").toUpperCase();
    },
    openPreview() {
      if (!this.request.fileUrl) return;
      this.showPreview = true;
    },
    toggle(payload) {
      this.isOpen = true;
      this.isLoading = true;
      this.showPreview = false;

      if (payload?.request) {
        this.employee = { ...(payload.employee || {}) };
        this.request = { ...payload.request };
        this.isLoading = false;
        return;
      }

      this.employee = { ...payload };
      this.request = {};

      this.$_getContractEmployeeDetails(payload.userId)
        .then(({ data }) => {
          this.request = data?.data?.latestRequest || {};
        })
        .catch((error) => {
          this.$toasted.error(error?.message || error, { duration: 5000 });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style scoped>
.date-readonly {
  height: 40px;
  background: #eeeeee;
  border: 1px solid #e5e7eb;
  border-radius: 5px;
}
.note-box {
  min-height: 72px;
  padding: 12px;
  background: #f5f5f5;
  border-radius: 5px;
  white-space: pre-wrap;
}
.preview-modal {
  width: min(720px, 92vw);
  max-height: 85vh;
}
.preview-body {
  min-height: 280px;
  max-height: 70vh;
}
</style>
